import React, { useState } from "react";
import PropTypes from "prop-types";
import analytics from "web/script/analytics/analytics";
import { LanguageChoicePropType } from "web/types/language";
import classnames from "classnames";
import withReduxProvider from "web/react/redux-provider";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { setSelectedGender } from "web/redux/ducks/main-navigation";
import SlideContainer from "./main-navigation-slide-container/main-navigation-slide-container";
import MainNavigationGenderMenu from "./main-navigation-gender-menu/main-navigation-gender-menu";
import MainNavigationSubCategory from "./main-navigation-subcategory/main-navigation-subcategory";
import BurgerMenuCountrySelector from "./../burger-menu/burger-menu-country-selector-menu/burger-menu-country-selector-menu";
import LanguageSelector from "./../burger-menu/burger-menu-language-selector/burger-menu-language-selector";
import MainNavigationDesktop from "./main-navigation-desktop/main-navigation-desktop";
import styles from "./main-navigation.css";

function extractNavigation(state) {
    return state.mainNavigationReducer;
}

function GenderButton({ title, isActive, onGenderClick }) {
    return (
        <button
            type="button"
            onClick={() => onGenderClick()}
            className={classnames(styles.genderOption, {
                [styles.genderActive]: isActive,
            })}
        >
            {title}
        </button>
    );
}

GenderButton.propTypes = {
    title: PropTypes.string,
    isActive: PropTypes.bool,
    onGenderClick: PropTypes.func,
};

export function MainNavigation({
    data,
    userGender,
    showCountryPicker,
    currentCountry,
    currentCurrency,
    allCountries,
    languageSelector,
    fullRender,
    featuredDesignersUrl,
}) {
    const dispatch = useDispatch();
    const { visible, selectedGender } = useReduxSelector(extractNavigation);
    const [categories, setCategories] = useState([]);
    const [menuIndex, setMenuIndex] = useState(0);
    const [renderCountries, setRenderCountries] = useState(false);
    const [renderLanguages, setRenderLanguages] = useState(false);
    const genderMenuArray = [data.F, data.M];
    const COUNTRY_SLIDE = 1;
    const LANGUAGE_SLIDE = 2;
    const genders = {
        women: "F",
        men: "M",
    };
    let isWomenMenu;
    let currentLanguage = languageSelector.choices.find(
        (language) => language.is_selected === true
    );

    function showNextCategory(nextCategory) {
        const newCategories = [...categories, nextCategory];
        setCategories(newCategories);
        setMenuIndex(newCategories.length);
        scrollToTop();
        analytics.event("nav", "clicked", `user_click_${nextCategory.name}`);
    }

    function goBackCategory() {
        categories.pop();
        setMenuIndex(categories.length);
        setCategories([...categories]);
        scrollToTop();
    }

    function scrollToTop() {
        const menuContent = document.getElementById("burgerMenuContent");
        if (menuContent !== null) {
            menuContent.scrollTop = 0;
        }
    }

    function showCountrySlide() {
        analytics.event("SETTINGS", "SHOW_COUNTRY_OVERLAY");
        setRenderCountries(true);
        setMenuIndex(COUNTRY_SLIDE);
        scrollToTop();
    }

    function hideCountrySlide() {
        setRenderCountries(false);
        setMenuIndex(0);
        scrollToTop();
    }

    function showLanguageSlide() {
        setRenderLanguages(true);
        setMenuIndex(LANGUAGE_SLIDE);
        scrollToTop();
    }

    function hideLanguageSlide() {
        setRenderLanguages(false);
        setMenuIndex(0);
        scrollToTop();
    }

    // Gender can be set from the request (userGender) or
    // from redux (selectedGender) by interacting with the navigation
    let currentGender = "women"; // default

    if (userGender) {
        currentGender = userGender.toLowerCase();
    }

    if (selectedGender !== "") {
        currentGender = selectedGender;
    }

    isWomenMenu = currentGender === "women";

    if (visible) {
        scrollToTop();
    }

    return (
        <>
            <nav
                id="burgerMenuContent"
                className={classnames(styles.nav, { [styles.navOpen]: visible })}
                aria-label="Lyst navigation menu"
            >
                <SlideContainer index={menuIndex}>
                    <div>
                        <div className={styles.genderSelector}>
                            <GenderButton
                                title={data.F.display_name}
                                isActive={isWomenMenu}
                                onGenderClick={() => {
                                    dispatch(setSelectedGender("women"));
                                }}
                            />
                            <GenderButton
                                title={data.M.display_name}
                                isActive={!isWomenMenu}
                                onGenderClick={() => {
                                    dispatch(setSelectedGender("men"));
                                }}
                            />
                        </div>

                        <div
                            className={classnames(styles.genderMenuContainer, {
                                [styles.genderMenuSwitch]: !isWomenMenu,
                            })}
                        >
                            {genderMenuArray.map((genderMenu) => (
                                <MainNavigationGenderMenu
                                    key={genderMenu.display_name}
                                    menu={genderMenu}
                                    fullRender={fullRender}
                                    showCountryPicker={showCountryPicker}
                                    onCategoryClick={showNextCategory}
                                    onCountrySelectorClick={showCountrySlide}
                                    currentCountry={currentCountry}
                                    currentCurrency={currentCurrency}
                                    currentLanguage={currentLanguage}
                                    onLanguageSelectorClick={showLanguageSlide}
                                    showLanguageSelector={languageSelector.is_shown}
                                />
                            ))}
                        </div>
                    </div>

                    {categories &&
                        categories.map((category) => {
                            return (
                                <MainNavigationSubCategory
                                    key={category.display_name}
                                    title={category.display_name}
                                    submenu={category}
                                    show={true}
                                    onCategoryClick={showNextCategory}
                                    onClose={goBackCategory}
                                />
                            );
                        })}

                    {renderCountries && (
                        <BurgerMenuCountrySelector
                            allCountries={allCountries}
                            currentCountry={currentCountry}
                            currentCurrency={currentCurrency}
                            onBackToMainClick={hideCountrySlide}
                        />
                    )}

                    {renderLanguages && (
                        <LanguageSelector
                            allLanguages={languageSelector.choices}
                            onBackToMainClick={hideLanguageSlide}
                        />
                    )}
                </SlideContainer>
            </nav>
            <MainNavigationDesktop
                menuData={data[genders[currentGender]]}
                showContent={visible}
                featuredDesignersUrl={featuredDesignersUrl}
            />
        </>
    );
}

MainNavigation.defaultProps = {
    data: [],
    userGender: "",
    showCountryPicker: false,
    currentCountry: "",
    currentCurrency: "",
    allCountries: [],
    fullRender: false,
};

MainNavigation.propTypes = {
    data: PropTypes.PropTypes.object,
    userGender: PropTypes.string,
    showCountryPicker: PropTypes.bool,
    currentCountry: PropTypes.string,
    currentCurrency: PropTypes.string,
    allCountries: PropTypes.array,
    languageSelector: PropTypes.shape({
        is_shown: PropTypes.bool.isRequired,
        callout_text: PropTypes.string.isRequired,
        choices: PropTypes.arrayOf(LanguageChoicePropType).isRequired,
        num_columns: PropTypes.string.isRequired,
    }),
    fullRender: PropTypes.bool,
    featuredDesignersUrl: PropTypes.string,
};

export default withReduxProvider(MainNavigation);
